import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Description as ClaimsIcon,
  People as ContactsIcon,
  Business as CompaniesIcon,
  Security as InsurersIcon,
  Person as ClientsIcon,
  Event as CalendarIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Build as ToolsIcon,
  AccountCircle as ProfileIcon,
  Badge as InsuranceRepIcon,
  TrendingUp as LeadsIcon,
  Campaign as MarketingIcon,
  Folder as FileIcon,
  FileUpload as ImportIcon,
  FileDownload as ExportIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Support as SupportIcon,
  QuestionAnswer as FAQIcon,
  Feedback as FeedbackIcon,
  MenuBook as DocsIcon,
  ManageAccounts as RolesIcon,
  Email as EmailTemplateIcon,
  Extension as IntegrationsIcon,
  RssFeed as NewsIcon,
  WbSunny as WeatherIcon,
  Map as MapIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from './Breadcrumb';
import { BreadcrumbContext } from './Breadcrumb';
import { useBreadcrumb } from '../App';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

export const Layout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [fileMenuAnchorEl, setFileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [helpMenuAnchorEl, setHelpMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [toolsMenuAnchorEl, setToolsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { lastItem } = useBreadcrumb();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFileMenuAnchorEl(event.currentTarget);
  };

  const handleHelpMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHelpMenuAnchorEl(event.currentTarget);
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchorEl(event.currentTarget);
  };

  const handleToolsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setToolsMenuAnchorEl(event.currentTarget);
  };

  const handleFileMenuClose = () => {
    setFileMenuAnchorEl(null);
  };

  const handleHelpMenuClose = () => {
    setHelpMenuAnchorEl(null);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchorEl(null);
  };

  const handleToolsMenuClose = () => {
    setToolsMenuAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { 
      text: 'File', 
      icon: <FileIcon />, 
      path: '/file',
      hasSubmenu: true,
      submenuItems: [
        { text: 'Import', icon: <ImportIcon />, path: '/file/import' },
        { text: 'Export', icon: <ExportIcon />, path: '/file/export' },
        { text: 'Backup', icon: <BackupIcon />, path: '/file/backup' },
        { text: 'Restore', icon: <RestoreIcon />, path: '/file/restore' },
      ]
    },
    { text: 'Claims Management', icon: <ClaimsIcon />, path: '/claims' },
    { text: 'Leads', icon: <LeadsIcon />, path: '/leads' },
    { text: 'Marketing', icon: <MarketingIcon />, path: '/marketing' },
    { text: 'Contacts', icon: <ContactsIcon />, path: '/contacts' },
    { text: 'Companies', icon: <CompaniesIcon />, path: '/companies' },
    { text: 'Insurers', icon: <InsurersIcon />, path: '/insurers' },
    { text: 'Insurance Representatives', icon: <InsuranceRepIcon />, path: '/insurance-representatives' },
    { text: 'Clients', icon: <ClientsIcon />, path: '/clients' },
    { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
    { 
      text: 'Tools', 
      icon: <ToolsIcon />, 
      path: '/tools',
      hasSubmenu: true,
      submenuItems: [
        { text: 'News Feeds', icon: <NewsIcon />, path: '/tools/news' },
        { text: 'Weather Feeds', icon: <WeatherIcon />, path: '/tools/weather' },
        { text: 'Claims Map', icon: <MapIcon />, path: '/tools/claims-map' },
      ]
    },
    { 
      text: 'Settings', 
      icon: <SettingsIcon />, 
      path: '/settings',
      hasSubmenu: true,
      submenuItems: [
        { text: 'Users', icon: <ProfileIcon />, path: '/settings/users' },
        { text: 'Roles', icon: <RolesIcon />, path: '/settings/roles' },
        { text: 'Company', icon: <CompaniesIcon />, path: '/settings/company' },
        { text: 'Email Templates', icon: <EmailTemplateIcon />, path: '/settings/email-templates' },
        { text: 'Integrations', icon: <IntegrationsIcon />, path: '/settings/integrations' },
      ]
    },
    { 
      text: 'Help', 
      icon: <HelpIcon />, 
      path: '/help',
      hasSubmenu: true,
      submenuItems: [
        { text: 'Documentation', icon: <DocsIcon />, path: '/help/documentation' },
        { text: 'FAQ', icon: <FAQIcon />, path: '/help/faq' },
        { text: 'Support', icon: <SupportIcon />, path: '/help/support' },
        { text: 'Feedback', icon: <FeedbackIcon />, path: '/help/feedback' },
      ]
    },
  ];

  const drawer = (isCollapsed = false) => (
    <div>
      <Toolbar sx={{ 
        justifyContent: 'center', 
        alignItems: 'center', 
        flexDirection: 'column', 
        minHeight: 80,
        padding: '8px'
      }}>
        {!isMobile && (
          <IconButton 
            onClick={handleDesktopDrawerToggle} 
            size="small"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={isCollapsed ? item.text : ''} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={(event) => {
                  if (item.hasSubmenu) {
                    if (item.text === 'File') {
                      handleFileMenuOpen(event);
                    } else if (item.text === 'Help') {
                      handleHelpMenuOpen(event);
                    } else if (item.text === 'Settings') {
                      handleSettingsMenuOpen(event);
                    } else if (item.text === 'Tools') {
                      handleToolsMenuOpen(event);
                    }
                  } else {
                    navigate(item.path);
                    if (isMobile) {
                      setMobileOpen(false);
                    }
                  }
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: isCollapsed ? 'center' : 'initial',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isCollapsed ? 'auto' : 3,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      
      {/* File Submenu */}
      <Menu
        anchorEl={fileMenuAnchorEl}
        open={Boolean(fileMenuAnchorEl)}
        onClose={handleFileMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {menuItems.find(item => item.text === 'File')?.submenuItems?.map((subItem) => (
          <MenuItem
            key={subItem.text}
            onClick={() => {
              handleFileMenuClose();
              navigate(subItem.path);
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
          >
            <ListItemIcon>
              {subItem.icon}
            </ListItemIcon>
            {subItem.text}
          </MenuItem>
        ))}
      </Menu>

      {/* Help Submenu */}
      <Menu
        anchorEl={helpMenuAnchorEl}
        open={Boolean(helpMenuAnchorEl)}
        onClose={handleHelpMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {menuItems.find(item => item.text === 'Help')?.submenuItems?.map((subItem) => (
          <MenuItem
            key={subItem.text}
            onClick={() => {
              handleHelpMenuClose();
              navigate(subItem.path);
              if (isMobile) setMobileOpen(false);
            }}
          >
            <ListItemIcon>{subItem.icon}</ListItemIcon>
            {subItem.text}
          </MenuItem>
        ))}
      </Menu>

      {/* Settings Submenu */}
      <Menu
        anchorEl={settingsMenuAnchorEl}
        open={Boolean(settingsMenuAnchorEl)}
        onClose={handleSettingsMenuClose}
        anchorOrigin={{ vertical:'top', horizontal:'right' }}
        transformOrigin={{ vertical:'top', horizontal:'left' }}
      >
        {menuItems.find(i=>i.text==='Settings')?.submenuItems?.map(sub=> (
          <MenuItem key={sub.text} onClick={()=>{
            handleSettingsMenuClose();
            navigate(sub.path);
            if(isMobile) setMobileOpen(false);
          }}>
            <ListItemIcon>{sub.icon}</ListItemIcon>{sub.text}
          </MenuItem>
        ))}
      </Menu>

      {/* Tools Submenu */}
      <Menu
        anchorEl={toolsMenuAnchorEl}
        open={Boolean(toolsMenuAnchorEl)}
        onClose={handleToolsMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {menuItems.find(item => item.text === 'Tools')?.submenuItems?.map((subItem) => (
          <MenuItem
            key={subItem.text}
            onClick={() => {
              handleToolsMenuClose();
              navigate(subItem.path);
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
          >
            <ListItemIcon>
              {subItem.icon}
            </ListItemIcon>
            {subItem.text}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );

  const currentDrawerWidth = desktopOpen ? drawerWidth : collapsedDrawerWidth;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Logo - Clickable link to Dashboard */}
          <Box
            component="img"
            src="/logo.png"
            alt="PA-CRM Logo"
            sx={{
              height: 40,
              width: 'auto',
              cursor: 'pointer',
              mr: 2,
              display: { xs: 'none', sm: 'block' }
            }}
            onClick={() => navigate('/dashboard')}
          />
          
          {/* Mobile Logo - Smaller version for mobile */}
          <Box
            component="img"
            src="/logo.png"
            alt="PA-CRM Logo"
            sx={{
              height: 32,
              width: 'auto',
              cursor: 'pointer',
              mr: 2,
              display: { xs: 'block', sm: 'none' }
            }}
            onClick={() => navigate('/dashboard')}
          />
          
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <BreadcrumbContext.Provider value={{ lastItem }}>
              <Breadcrumb showHome={true} />
            </BreadcrumbContext.Provider>
          </Box>
          <Typography variant="h6" noWrap component="div" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
            PA-CRM
          </Typography>
          {/* Owner Icon - Only visible for owners */}
          {user?.role?.toLowerCase() === 'owner' && (
            <Tooltip title="Owner Portal">
              <IconButton
                size="large"
                edge="end"
                aria-label="owner portal"
                onClick={() => navigate('/owner')}
                color="inherit"
                sx={{ mr: 1 }}
              >
                <TrophyIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar 
              sx={{ width: 32, height: 32 }}
              src={user?.profileImage ? `${import.meta.env.VITE_API_URL || ''}${user.profileImage}` : undefined}
            >
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              navigate('/profile');
            }}>
              <ListItemIcon>
                <ProfileIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: theme.transitions.create(['width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer(!desktopOpen)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          mt: '64px',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children ? children : <Outlet />}
      </Box>
    </Box>
  );
}; 